/*
 * PBR v2 — scroll reveal
 * Adds .pbr-reveal--in to any .pbr-reveal element as it enters the viewport,
 * driving the fade/rise transition defined in pbr-theme.css. Marking <html>
 * with .pbr-js hands control to this observer and disables the CSS-only
 * fallback. Respects prefers-reduced-motion and degrades safely when
 * IntersectionObserver is unavailable.
 */
(() => {
  const root = document.documentElement;

  const reducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // No observer support, or motion is unwelcome: don't hide anything.
  // Leave .pbr-js off so the CSS safety net reveals content immediately.
  if (reducedMotion || typeof IntersectionObserver === 'undefined') {
    return;
  }

  const reveal = (el) => el.classList.add('pbr-reveal--in');

  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          obs.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  const overlapsViewport = (rect) =>
    rect.top < window.innerHeight && rect.bottom > 0;

  // Reveal what is already on screen, observe the rest. Every layout read
  // happens before any class write: a read between writes forces a style
  // flush in which the element is hidden, and the reveal then transitions up
  // from opacity 0 instead of holding steady.
  const observeAll = (nodes) => {
    const rects = Array.from(nodes, (el) => el.getBoundingClientRect());
    nodes.forEach((el, i) => {
      if (overlapsViewport(rects[i])) {
        reveal(el);
      } else {
        observer.observe(el);
      }
    });
  };

  const init = () => {
    const nodes = document.querySelectorAll('.pbr-reveal:not(.pbr-reveal--in)');
    // Measure before .pbr-js lands. Until then the CSS fallback keeps
    // above-the-fold content visible; measuring after would flush the hidden
    // .pbr-js state and every on-screen element would blink out and fade
    // back in on load.
    const rects = Array.from(nodes, (el) => el.getBoundingClientRect());
    // Signal to CSS that JS owns the reveal timing.
    root.classList.add('pbr-js');
    nodes.forEach((el, i) => {
      if (overlapsViewport(rects[i])) {
        reveal(el);
      } else {
        observer.observe(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Sections added by the theme editor should animate too.
  document.addEventListener('shopify:section:load', (event) => {
    const scope = event.target || document;
    observeAll(scope.querySelectorAll('.pbr-reveal:not(.pbr-reveal--in)'));
  });

  // Elements injected after load (collection infinite scroll, filter/sort
  // re-renders) would otherwise stay hidden — watch for them and observe.
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches('.pbr-reveal:not(.pbr-reveal--in)')) {
          observeAll([node]);
        }
        observeAll(node.querySelectorAll('.pbr-reveal:not(.pbr-reveal--in)'));
      }
    }
  });
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

/*
 * PBR v2 — image load retry
 * An <img> that errors (network blip, dropped connection mid-scroll, CDN
 * hiccup) never retries on its own and sticks as a broken icon until reload.
 * Retry Shopify CDN images a few times with backoff. Error events don't
 * bubble, so listen in the capture phase.
 */
(() => {
  const MAX_RETRIES = 3;
  const CDN_IMAGE = /\/cdn\/shop\//;

  const retryImage = (img) => {
    // Attribute values, not resolved properties: also skips the spurious
    // error fired while src/srcset are removed during a retry below.
    const src = img.getAttribute('src');
    const srcset = img.getAttribute('srcset');
    if (!src || !CDN_IMAGE.test(src + ' ' + (srcset || ''))) return;

    const attempt = Number(img.dataset.pbrRetry || 0);
    if (attempt >= MAX_RETRIES) return;
    img.dataset.pbrRetry = attempt + 1;

    setTimeout(() => {
      // Clear then restore: re-setting the same value can be ignored,
      // removing the attributes forces a fresh load attempt.
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', src);
    }, 500 * 2 ** attempt);
  };

  document.addEventListener(
    'error',
    (event) => {
      if (event.target instanceof HTMLImageElement) retryImage(event.target);
    },
    true
  );

  // This script is deferred, so images that errored during the initial page
  // load failed before the listener existed. They sit complete-but-empty
  // (a pending lazy image reports complete === false) — sweep them now.
  for (const img of document.images) {
    if (img.complete && img.naturalWidth === 0) retryImage(img);
  }
})();
